import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
  RouterProvider,
} from 'react-router-dom';
import { useLocation } from 'react-router';

import { URLS } from 'config/urls';

import SetPassword from 'pages/SetPassword';
import Login from 'pages/Login';
import NotFound from 'pages/NotFound';
import Flights from 'pages/Flights';
import FlightOffer from 'pages/FlightOffer';
import { ForgotPasswordPage } from 'pages/ForgotPasswordPage';
import { ProcessingPayment } from 'pages/ProcessingPayment';
import { useAuth } from 'entities/Auth';
import { REDIRECT_PARAMETER_KEY } from 'utils/constants.ts';

const ENABLE_FLIGHTS_FEATURE = import.meta.env.VITE_ENABLE_FLIGHTS_FEATURE;

const ProtectedRoute = ({ redirectPath = '/login' }: { redirectPath?: string }) => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    const redirectTo = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`${redirectPath}?redirectTo=${redirectTo}`} replace />;
  }

  return <Outlet />;
};

const PublicRoutes = ({ redirectPath = URLS.FLIGHTS }: { redirectPath?: string }) => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  if (isAuthenticated) {
    const redirectTo = new URLSearchParams(location.search).get(REDIRECT_PARAMETER_KEY) || redirectPath;
    return <Navigate to={redirectTo} replace />;
  }

  return <Outlet />;
};

const RootRoutes = () => {
  const { isAuthenticated } = useAuth();

  return isAuthenticated ? <Navigate to={URLS.FLIGHTS} replace /> : <Navigate to={URLS.LOGIN} replace />;
};

const routes = createRoutesFromElements(
  <>
    <Route path='/' element={<RootRoutes />} />
    <Route path={URLS.SET_PASSWORD} Component={SetPassword} />
    <Route path={URLS.FORGOT_PASSWORD} Component={ForgotPasswordPage} />
    <Route element={<PublicRoutes />}>
      {ENABLE_FLIGHTS_FEATURE == 'true' && <Route path={URLS.LOGIN} Component={Login} />}
    </Route>
    <Route element={<ProtectedRoute />}>
      {ENABLE_FLIGHTS_FEATURE == 'true' && (
        <>
          <Route path={URLS.FLIGHTS} Component={Flights} />
          <Route path={URLS.FLIGHT_OFFER} Component={FlightOffer} />
          <Route path={URLS.PROCESSING_PAYMENT} Component={ProcessingPayment} />
        </>
      )}
    </Route>
    <Route path='*' Component={NotFound} />
  </>
);

const router = createBrowserRouter(routes);

const Router = () => <RouterProvider router={router} />;

export default Router;
